import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LayoutPanelTop, ArrowUp, ArrowDown } from "lucide-react";
import { api, resolveMedia } from "../../lib/api";
import { inputCls, errMsg, UploadPicker } from "./shared";

const EMPTY = { title: "", description: "", button_text: "", button_link: "", image: "", active: true, order: 0 };

export default function BannerManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/banners").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) {
      toast.error("कृपया बैनर इमेज अपलोड करें");
      return;
    }
    try {
      if (editing) {
        await api.put(`/admin/banners/${editing}`, form);
        toast.success("बैनर अपडेट हुआ");
      } else {
        await api.post("/admin/banners", { ...form, order: items.length });
        toast.success("बैनर जोड़ा गया");
      }
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("क्या आप यह बैनर हटाना चाहते हैं?")) return;
    try {
      await api.delete(`/admin/banners/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`/admin/banners/${b.id}`, { ...b, active: !b.active });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const move = async (idx, dir) => {
    const swap = items[idx + dir];
    if (!swap) return;
    const a = items[idx];
    try {
      await api.put(`/admin/banners/${a.id}`, { ...a, order: swap.order });
      await api.put(`/admin/banners/${swap.id}`, { ...swap, order: a.order });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <button data-testid="banner-new" onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(!showForm); }} className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">
        <Plus size={16} /> नया बैनर जोड़ें
      </button>

      {showForm && (
        <form data-testid="banner-form" onSubmit={save} className="bg-white rounded-2xl border border-ngo-deep/10 p-6 mb-8 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">बैनर इमेज *</label>
            <div className="flex gap-2 items-center">
              <input data-testid="banner-image" className={inputCls} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="इमेज अपलोड करें या URL डालें" />
              <UploadPicker testid="banner-image-upload" context="banners" label="अपलोड" onUploaded={(url) => setForm((p) => ({ ...p, image: url }))} />
            </div>
            {form.image && <img src={resolveMedia(form.image)} alt="बैनर" className="mt-3 h-28 rounded-xl object-cover border border-ngo-deep/10" />}
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">बैनर शीर्षक *</label>
            <input data-testid="banner-title" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">संक्षिप्त विवरण</label>
            <input data-testid="banner-description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">बटन टेक्स्ट</label>
            <input data-testid="banner-button-text" className={inputCls} value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} placeholder="दान करें" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">बटन लिंक</label>
            <input data-testid="banner-button-link" className={inputCls} value={form.button_link} onChange={(e) => setForm({ ...form, button_link: e.target.value })} placeholder="/donate" />
          </div>
          <label className="flex items-center gap-2.5 text-sm font-semibold text-ngo-ink">
            <input data-testid="banner-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-[18px] h-[18px] accent-ngo-deep" />
            वेबसाइट पर दिखाएं (सक्रिय)
          </label>
          <div className="sm:col-span-2 flex gap-3">
            <button data-testid="banner-save" className="px-7 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">{editing ? "अपडेट करें" : "बैनर जोड़ें"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-6 py-3 rounded-full border border-ngo-deep/20 text-ngo-deep text-sm font-semibold">रद्द करें</button>
          </div>
        </form>
      )}

      {items.length === 0 && <p data-testid="banner-empty" className="text-center text-ngo-ink/50 py-14">अभी कोई बैनर नहीं है।</p>}
      <div data-testid="banner-list" className="space-y-4">
        {items.map((b, idx) => (
          <div key={b.id} className="bg-white rounded-2xl border border-ngo-deep/10 p-4 flex flex-wrap items-center gap-4">
            <img src={resolveMedia(b.image)} alt={b.title} className="w-28 h-16 object-cover rounded-lg" />
            <div className="flex-1 min-w-[180px]">
              <p className="font-semibold text-ngo-ink flex items-center gap-2">
                <LayoutPanelTop size={15} className="text-ngo-deep" /> {b.title}
              </p>
              <p className="text-xs mt-1">
                {b.active ? <span className="text-ngo-deep font-bold">● सक्रिय</span> : <span className="text-ngo-ink/40 font-bold">● निष्क्रिय</span>}
                {b.button_text && <span className="text-ngo-ink/50"> • बटन: {b.button_text} → {b.button_link}</span>}
              </p>
            </div>
            <div className="flex gap-1.5">
              <button data-testid={`banner-up-${b.id}`} onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="ऊपर" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center disabled:opacity-30 hover:bg-ngo-leaf/25 transition-colors duration-200"><ArrowUp size={15} /></button>
              <button data-testid={`banner-down-${b.id}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1} aria-label="नीचे" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center disabled:opacity-30 hover:bg-ngo-leaf/25 transition-colors duration-200"><ArrowDown size={15} /></button>
              <button data-testid={`banner-toggle-${b.id}`} onClick={() => toggleActive(b)} className={`px-4 h-9 rounded-full text-xs font-bold transition-colors duration-200 ${b.active ? "bg-ngo-deep text-white" : "bg-ngo-ink/10 text-ngo-ink/60"}`}>
                {b.active ? "सक्रिय" : "निष्क्रिय"}
              </button>
              <button data-testid={`banner-edit-${b.id}`} onClick={() => { setEditing(b.id); setForm({ title: b.title, description: b.description, button_text: b.button_text, button_link: b.button_link, image: b.image, active: b.active, order: b.order }); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="संपादित करें" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-leaf/25 transition-colors duration-200"><Pencil size={15} /></button>
              <button data-testid={`banner-delete-${b.id}`} onClick={() => remove(b.id)} aria-label="हटाएं" className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors duration-200"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
